import { createStore, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useMemo } from 'react'
import { deepCompare, useLifecyclePersistence, useSyncLifecycleToExistingAtom } from '../../../utilities'
import { ConformedBranding, MediaAsset } from '../../../views'
import {
	brandLibraryActiveAtom,
	brandLibraryAtom,
	brandLibraryLoadingAtom,
	deleteBrandingCallbackAtom,
	deletingBrandingIdAtom,
	expandedBrandingAtom,
	expandedBrandingIdAtom,
	expandedBrandingLogoCallbackAtom,
	expandedBrandingSavingAtom,
	expandedBrandingScanErrorAtom,
	expandedBrandingStatusAtom,
	expandedBrandingUrlCallbackAtom,
	newBrandingCallbackAtom,
	useExpandedBrandingListener,
} from './state'

export type UseBrandLibraryArgs = {
	onWakeUp: () => void // this should initiate fetching the brand sets
	onSleep?: () => void // this _may_ choose to stop updating the brand data

	brandingSets?: Array<ConformedBranding>

	isPending?: boolean

	onSetExpandedBrandingId?: (id: string) => void
	expandedBrandingSet?: ConformedBranding
	expandedBrandingVersion?: number | string | Date | null
	isExpandedBrandingGenerating?: boolean
	isExpandedBrandingPending?: boolean
	isExpandedBrandingReady?: boolean
	expandedBrandingScanError?: string | null

	onScanUrl?: (url: string, onSuccess: () => void, onError: (errorKey: string) => void) => void
	onNew?: (onSuccess: (brandingId: string) => void) => void

	onLogoUpload?: (logo: File, onSuccess: (media: MediaAsset) => void, onFail?: () => void) => void

	onDelete?: (brandingId: string, onSuccess: () => void) => void
	deletingBrandingId?: string | null

	// Save callback should persist the expanded branding.
	// May be sync or async; resolve indicates completion.
	onExpandedBrandingChange?: (branding: ConformedBranding) => void | Promise<void>
}

/**
 * Called by consumers (views) to set brand information. Internally, access atoms in the store.
 */
export function useBrandLibrary(
	{
		onWakeUp = () => {},
		onSleep = () => {},
		brandingSets = [],
		isPending = false, // Should reflect tanstack `isPending`
		onSetExpandedBrandingId,
		expandedBrandingSet,
		expandedBrandingVersion,
		isExpandedBrandingGenerating,
		isExpandedBrandingPending,
		isExpandedBrandingReady,
		expandedBrandingScanError = null,
		onExpandedBrandingChange,
		onScanUrl,
		onNew,
		onLogoUpload,
		onDelete,
		deletingBrandingId = null,
	}: UseBrandLibraryArgs,
) {
	// retain store unless this hook's owner is remounted
	const store = useMemo(() => createStore(), [])

	const [brandLibrary, setBrandLibrary] = useAtom(brandLibraryAtom, { store })
	const setBrandLibraryLoading = useSetAtom(brandLibraryLoadingAtom, { store })
	const isActive = useAtomValue(brandLibraryActiveAtom, { store })
	const setExpandedBrandingUrlCallback = useSetAtom(expandedBrandingUrlCallbackAtom, { store })
	const setNewBrandingCallback = useSetAtom(newBrandingCallbackAtom, { store })
	const setExpandedBrandingLogoCallback = useSetAtom(expandedBrandingLogoCallbackAtom, { store })
	const setDeleteBrandingCallback = useSetAtom(deleteBrandingCallbackAtom, { store })
	const setDeletingBrandingId = useSetAtom(deletingBrandingIdAtom, { store })
	const setExpandedBrandingScanError = useSetAtom(expandedBrandingScanErrorAtom, { store })

	useEffect(() => {
		if (isActive) {
			onWakeUp()
		} else {
			onSleep()
		}
	}, [
		isActive,
		onWakeUp,
	])

	// sync loading state
	useEffect(() => {
		setBrandLibraryLoading(isPending)
	}, [isPending, setBrandLibraryLoading])

	// syncing callbacks
	useEffect(() => {
		if (onScanUrl) {
			setExpandedBrandingUrlCallback(() => onScanUrl)
		} else {
			setExpandedBrandingUrlCallback(null)
		}
	}, [onScanUrl, setExpandedBrandingUrlCallback])

	useEffect(() => {
		setNewBrandingCallback(() => {
			return onNew ?? null
		})
	}, [onNew, setNewBrandingCallback])

	useEffect(() => {
		setExpandedBrandingLogoCallback(() => onLogoUpload ?? null)
	}, [onLogoUpload, setExpandedBrandingLogoCallback])

	useEffect(() => {
		setDeleteBrandingCallback(() => onDelete ?? null)
	}, [onDelete, setDeleteBrandingCallback])

	useEffect(() => {
		setDeletingBrandingId(deletingBrandingId)
	}, [deletingBrandingId, setDeletingBrandingId])

	useEffect(() => {
		setExpandedBrandingScanError(expandedBrandingScanError)
	}, [expandedBrandingScanError, setExpandedBrandingScanError])

	// sync brand library
	useEffect(() => {
		if (!deepCompare(brandLibrary, brandingSets)) {
			setBrandLibrary(brandingSets)
		}
	}, [brandingSets, brandLibrary, setBrandLibrary])

	// expanded stuff
	const [expandedBrandingId] = useAtom(expandedBrandingIdAtom, { store })
	const [expandedBranding] = useAtom(expandedBrandingAtom, { store })

	// sync to owner
	useEffect(() => {
		if (expandedBrandingId) {
			onSetExpandedBrandingId?.(expandedBrandingId)
		}
	}, [expandedBrandingId, onSetExpandedBrandingId])

	// New lifecycle + persistence management
	const expandedLifecycle = useLifecyclePersistence<ConformedBranding>({
		data: expandedBrandingSet ?? undefined,
		isIdle: (d) => d === undefined,
		isPending: () => isExpandedBrandingPending ?? false,
		isGenerating: () => isExpandedBrandingGenerating ?? false,
		save: async (branding) => {
			await (onExpandedBrandingChange?.(branding) ?? Promise.resolve())
		},
		debounceMs: 500,
		getVersion: () => expandedBrandingVersion ?? null,
		debug: false,
		name: 'useBrandLibrary',
	})

	// Direct wiring: use the existing expandedBrandingAtom and its listener
	useSyncLifecycleToExistingAtom<ConformedBranding>(
		expandedLifecycle,
		{
			dataAtom: expandedBrandingAtom,
			useDataListener: useExpandedBrandingListener,
			lifecycleAtom: expandedBrandingStatusAtom,
			persistenceAtom: expandedBrandingSavingAtom,
		},
		{ store },
	)

	return { store }
}
